<template>
  <AIAssistantBaseChatTransition
    appear
    class="tw:grid tw:gap-4"
  >
    <QueryEditor
      data-index="0"
      data-testid="query-editor"
      disable-count
      hide-controls
      hide-help-button
      hide-label
      :model-value="message.stream.booleanQuery"
      preview
      read-only
    />

    <div
      class="tw:grid tw:gap-2"
      data-index="1"
    >
      <AIAssistantBaseOption
        v-for="(option, optionIndex) in refinementOptions"
        :key="optionIndex"
        :data-index="optionIndex"
        :data-testid="option.id"
        :disabled="disabled"
        @click="emit('refine', option.label)"
        @mouseenter="handleMouseenterRefinementOption(option.value)"
        @mouseleave="handleMouseleaveRefinementOption"
      >
        {{ option.label }}

        <Popper
          v-if="!disabled"
          caret
          :data-testid="`${option.id}-popper`"
          :model-value="refinementOptionPopper === option.value"
          @click.stop
          @mouseleave="handleMouseleaveRefinementOption"
          @update:model-value="refinementOptionPopper = ''"
        >
          <div
            class="tw:flex tw:w-64 tw:flex-col tw:items-center tw:gap-2 tw:rounded tw:bg-white tw:p-2 tw:shadow-[0px_4px_4px_0px_rgba(62,72,96,0.10)] tw:shadow-[0px_6px_6px_0px_rgba(62,72,96,0.15)] tw:outline tw:outline-1 tw:outline-offset-[-1px] tw:outline-denim-100"
          >
            <span class="tw:text-xs">{{ option.tooltipText }}</span>
            <BaseButton
              class="tw:w-full tw:text-xs! tw:text-blue-700!"
              :href="option.link"
              icon="send"
              icon-class="tw:text-blue-700"
              icon-right
              :label="getLocaleText('boolean_creator_boolean_query.learn_more')"
              new-design
              new-icon
              rel="noopener noreferrer"
              target="_blank"
              type="a"
            />
          </div>
        </Popper>
      </AIAssistantBaseOption>
    </div>

    <div
      class="tw:flex tw:gap-2"
      data-index="2"
    >
      <BaseButton
        class="tw:flex-1"
        color="secondary"
        data-testid="clipboard-button"
        icon="task"
        :label="
          getLocaleText('boolean_creator_boolean_query.use_the_boolean_query')
        "
        new-design
        new-icon
        @click="handleUseButton"
      />
      <BaseButton
        class="tw:flex-1"
        color="secondary"
        data-testid="restart-button"
        icon="circle-arrow"
        :label="getLocaleText('boolean_creator_boolean_query.start_over')"
        new-design
        new-icon
        @click="handleRestartButton"
      />
    </div>
  </AIAssistantBaseChatTransition>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { type Ref, computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { getLocaleText } from "shared/boot/i18n";
import AIAssistantBaseChatTransition from "shared/components/AI/AIAssistant/AIAssistantBaseChatTransition.vue";
import AIAssistantBaseOption from "shared/components/AI/AIAssistant/AIAssistantBaseOption.vue";
import { BaseButton } from "shared/components/base";
import Popper from "shared/components/base/Popper.vue";
import QueryEditor from "shared/components/QueryEditor/QueryEditor.vue";
import {
  type AIAssistantModelMessage,
  type AIAssistantSuggestMessageOption,
} from "shared/composables/useAIAssistant";
import { safePush } from "shared/helpers/routing";
import DialogService from "shared/modals/DialogService";
import { type AppConfig } from "shared/services/appConfig/types";
import useAIStore from "shared/stores/ai";
import useGlobalStore from "shared/stores/global";

interface BooleanCreatorQueryResponseProps {
  disabled?: boolean;
  message: AIAssistantModelMessage;
}

interface BooleanCreatorOption extends AIAssistantSuggestMessageOption {
  id: string;
  link: string;
  value: string;
}

const router = useRouter();
const route = useRoute();
const safeRouterPush = safePush({ router });

const aiStore = useAIStore();
const { aiBooleanCreatorQueryEditorValue } = storeToRefs(aiStore);

const { disabled, message } = defineProps<BooleanCreatorQueryResponseProps>();

const emit = defineEmits<{ refine: [string]; restart: [] }>();

const { appConfig } = storeToRefs(useGlobalStore()) as unknown as {
  appConfig: Ref<AppConfig>;
};

const refinementOptionPopper = ref<string>("");

const refinementOptions = computed<BooleanCreatorOption[]>(() => [
  {
    id: "expand-button",
    label: getLocaleText("boolean_creator_boolean_query.expand_label"),
    tooltipText: getLocaleText("boolean_creator_boolean_query.expand_tooltip"),
    value: "expand",
    link: appConfig.value.urls.booleanCreator.expandHelp,
  },
  {
    id: "refinement-button",
    label: getLocaleText("boolean_creator_boolean_query.refine_label"),
    tooltipText: getLocaleText("boolean_creator_boolean_query.refine_tooltip"),
    value: "refine",
    link: appConfig.value.urls.booleanCreator.refineHelp,
  },
]);

function handleMouseenterRefinementOption(value: string) {
  if (disabled) return;
  refinementOptionPopper.value = value;
}

function handleMouseleaveRefinementOption() {
  refinementOptionPopper.value = "";
}

function handleUseButton() {
  if (route.name === "mention-stream-new") {
    DialogService.confirm({
      title: getLocaleText(
        "boolean_creator_boolean_query.use_boolean_query_title"
      ),
      message: getLocaleText(
        "boolean_creator_boolean_query.use_boolean_query_message"
      ),
    }).onOk(() => {
      confirmUseBooleanQuery();
    });
  } else {
    confirmUseBooleanQuery();
  }
}

function confirmUseBooleanQuery() {
  aiBooleanCreatorQueryEditorValue.value = message.stream.booleanQuery;

  safeRouterPush({
    name: "mention-stream-new",
    query: { booleanCreator: true },
  });
}

function handleRestartButton() {
  DialogService.confirm({
    title: getLocaleText("boolean_creator_boolean_query.restart_title"),
    message: getLocaleText("boolean_creator_boolean_query.restart_message"),
  }).onOk(() => {
    emit("restart");
  });
}
</script>
